import { XIcon } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

interface ResizableSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title?: React.ReactNode;
	/** Action buttons rendered in header next to close button */
	actions?: React.ReactNode;
	/** Default width in px (default: 520) */
	defaultWidth?: number;
	/** Min width in px (default: 380) */
	minWidth?: number;
	/** Max width in px (default: 1200) */
	maxWidth?: number;
	/** Whether content area should scroll (default: true). Set false for iframe content. */
	scrollable?: boolean;
	children: React.ReactNode;
}

export function ResizableSheet({
	open,
	onOpenChange,
	title,
	actions,
	defaultWidth = 520,
	minWidth = 380,
	maxWidth = 1200,
	scrollable = true,
	children,
}: ResizableSheetProps) {
	const [panelWidth, setPanelWidth] = useState(defaultWidth);
	const isDragging = useRef(false);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			isDragging.current = true;
			const startX = e.clientX;
			const startWidth = panelWidth;

			const onMouseMove = (ev: MouseEvent) => {
				if (!isDragging.current) return;
				const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + (startX - ev.clientX)));
				setPanelWidth(newWidth);
			};

			const onMouseUp = () => {
				isDragging.current = false;
				document.removeEventListener("mousemove", onMouseMove);
				document.removeEventListener("mouseup", onMouseUp);
			};

			document.addEventListener("mousemove", onMouseMove);
			document.addEventListener("mouseup", onMouseUp);
		},
		[panelWidth, minWidth, maxWidth],
	);

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				showCloseButton={false}
				className="!max-w-none flex flex-col"
				style={{
					width: `min(${panelWidth}px, 100dvw)`,
					// Scale font size: 12px at 380px → 16px at 1200px
					fontSize: `clamp(12px, ${Math.round(10 + (panelWidth - 380) * 0.007)}px, 16px)`,
				}}
			>
				{/* Resize handle */}
				{/* biome-ignore lint/a11y/noStaticElementInteractions: resize drag handle */}
				<div
					onMouseDown={handleMouseDown}
					className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 transition-colors z-50 max-md:hidden"
				/>
				<SheetHeader className="border-b pb-4">
					<div className="flex items-center justify-between gap-2">
						{title ? (
							<SheetTitle className="text-base pr-6 flex-1 min-w-0">{title}</SheetTitle>
						) : (
							<div className="flex-1" />
						)}
						<div className="flex items-center gap-1.5 shrink-0">
							{actions}
							<SheetClose className="inline-flex h-8 w-8 items-center justify-center rounded-md opacity-70 ring-offset-background transition-opacity hover:opacity-100 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none max-md:size-10">
								<XIcon className="h-4 w-4" />
								<span className="sr-only">Zamknij</span>
							</SheetClose>
						</div>
					</div>
				</SheetHeader>
				<div className={`flex-1 min-h-0 ${scrollable ? "overflow-y-auto" : ""}`}>{children}</div>
			</SheetContent>
		</Sheet>
	);
}
